import React from 'react';
import { Link } from 'react-router-dom';
import { predictorPillars, predictorScreens } from '../../mock';
import { Sparkles, ArrowUpRight, TrendingUp, Search, Filter } from 'lucide-react';
import PredictorDashboardMock from './PredictorDashboardMock';

export default function Predictor() {
  return (
    <section id="predictor" className="py-20 lg:py-32 bg-[#F1EDE4] text-[#0F1C40]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-12 lg:mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#0F1C40]/60 mb-4">
              <span className="w-6 h-px bg-[#4ECDC4]" />
              <span>Our Flagship Product</span>
            </div>
            <div className="flex items-baseline gap-3">
              <h2 className="text-6xl lg:text-8xl xl:text-9xl tracking-tight leading-none font-serif italic text-[#0F1C40]">Predictor</h2>
              <span className="text-3xl lg:text-5xl text-[#4ECDC4] font-serif italic">.com</span>
            </div>
            <p className="mt-6 text-lg lg:text-xl text-[#0F1C40]/70 font-serif italic">White-Labeled, AI-Driven ABM Platform</p>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#0F1C40]/70 text-[15px] leading-relaxed max-w-md">
              Predict. Prioritize. Convert. Engineered for enterprise demand teams that need signal, not noise.
            </p>
            <Link
              to="/predictor"
              className="mt-6 inline-flex items-center gap-2 bg-[#0F1C40] text-[#F1EDE4] pl-5 pr-4 py-3 rounded-full text-sm font-semibold hover:bg-[#4ECDC4] hover:text-[#0F1C40] transition-colors"
              data-testid="predictor-explore-link"
            >
              Explore Predictor.com
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
          </div>
        </div>

        {/* Flagship pixel-perfect dashboard mockup */}
        <div className="reveal mb-6">
          <PredictorDashboardMock />
        </div>

        {/* Secondary dashboard panel — pipeline velocity spotlight */}
        <div className="relative bg-[#0F1C40] rounded-2xl p-6 lg:p-10 overflow-hidden reveal">
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(78,205,196,0.4), transparent 50%), radial-gradient(circle at 80% 80%, rgba(78,205,196,0.2), transparent 50%)' }} />
          <div className="relative grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4 space-y-4">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-white/50">
                <Sparkles className="w-3.5 h-3.5 text-[#4ECDC4]" />
                <span>Live Preview · predictor.com</span>
              </div>
              <div className="text-[#F1EDE4] text-2xl lg:text-3xl font-medium leading-tight">Pipeline Velocity <span className="text-white/50">· Last 12 weeks</span></div>
              <div className="flex items-baseline gap-3">
                <span className="text-5xl lg:text-6xl text-[#F1EDE4] font-serif italic tabular">+42.6%</span>
                <span className="text-[#4ECDC4] flex items-center gap-1 text-sm font-mono"><TrendingUp className="w-3.5 h-3.5" /> vs. Q1</span>
              </div>
              <div className="text-white/60 text-sm max-w-xs">Multi-signal accounts moved 3.1x faster from MQL to SQL vs. baseline.</div>
            </div>

            <div className="lg:col-span-8 bg-white/5 rounded-xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-white/50 text-xs font-mono uppercase tracking-wider">
                  <span className="w-2 h-2 rounded-full bg-[#4ECDC4] pulse-dot" />
                  <span>Pipeline Velocity — X55</span>
                </div>
                <div className="flex gap-2">
                  {['1W', '4W', '12W', 'YTD'].map((t, i) => (
                    <span key={t} className={`text-[11px] px-2 py-1 rounded font-mono ${i === 2 ? 'bg-[#4ECDC4] text-[#0F1C40]' : 'text-white/50'}`}>{t}</span>
                  ))}
                </div>
              </div>
              <svg viewBox="0 0 600 220" className="w-full h-48">
                <defs>
                  <linearGradient id="gline" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#4ECDC4" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#4ECDC4" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[0, 1, 2, 3].map((i) => (
                  <line key={i} x1="0" x2="600" y1={40 + i * 45} y2={40 + i * 45} stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />
                ))}
                <path d="M0,180 L50,160 L100,170 L150,140 L200,130 L250,110 L300,120 L350,85 L400,70 L450,60 L500,45 L550,35 L600,20 L600,220 L0,220 Z" fill="url(#gline)" />
                <path d="M0,180 L50,160 L100,170 L150,140 L200,130 L250,110 L300,120 L350,85 L400,70 L450,60 L500,45 L550,35 L600,20" fill="none" stroke="#4ECDC4" strokeWidth="2" />
                {[[100, 170], [200, 130], [400, 70], [600, 20]].map(([x, y], i) => (
                  <g key={i}>
                    <circle cx={x} cy={y} r="4" fill="#F1EDE4" />
                    <circle cx={x} cy={y} r="8" fill="#4ECDC4" fillOpacity="0.2" />
                  </g>
                ))}
              </svg>
            </div>
          </div>
        </div>

        {/* 3 additional screen previews */}
        <div className="grid md:grid-cols-3 gap-6 mt-6">
          {/* Screen 1: In-market accounts */}
          <div className="bg-[#0F1C40] text-[#F1EDE4] rounded-2xl p-6 hover:scale-[1.02] transition-transform reveal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4]"><Search className="w-3 h-3" /><span>In-market accounts</span></div>
              <span className="text-[10px] font-mono text-white/40">Screen 01</span>
            </div>
            <div className="text-4xl font-serif italic tabular">{predictorScreens[0].metric}</div>
            <div className="text-xs text-white/60 mb-4">{predictorScreens[0].metricLabel}</div>
            <div className="space-y-2">
              {['Boeing', 'AbbVie', 'Cognizant'].map((a, i) => (
                <div key={a} className="flex items-center justify-between text-xs bg-white/5 rounded px-3 py-2">
                  <span>{a}</span>
                  <span className="font-mono text-[#4ECDC4]">{[93, 67, 63][i]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Screen 2: Buying committee */}
          <div className="bg-[#0F1C40] text-[#F1EDE4] rounded-2xl p-6 hover:scale-[1.02] transition-transform reveal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4]"><Filter className="w-3 h-3" /><span>Buying committee</span></div>
              <span className="text-[10px] font-mono text-white/40">Screen 02</span>
            </div>
            <div className="text-4xl font-serif italic tabular">{predictorScreens[1].metric}</div>
            <div className="text-xs text-white/60 mb-4">{predictorScreens[1].metricLabel}</div>
            <div className="space-y-1.5">
              {[['CIO', 92], ['VP Eng', 84], ['Dir IT', 78], ['Head Sec', 65]].map(([role, score]) => (
                <div key={role}>
                  <div className="flex justify-between text-[11px] mb-1"><span>{role}</span><span className="font-mono text-[#4ECDC4]">{score}%</span></div>
                  <div className="h-1 bg-white/10 rounded-full"><div className="h-full bg-[#4ECDC4] rounded-full" style={{ width: `${score}%` }} /></div>
                </div>
              ))}
            </div>
          </div>

          {/* Screen 3: White label */}
          <div className="bg-[#0F1C40] text-[#F1EDE4] rounded-2xl p-6 hover:scale-[1.02] transition-transform reveal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4]"><Sparkles className="w-3 h-3" /><span>White label</span></div>
              <span className="text-[10px] font-mono text-white/40">Screen 03</span>
            </div>
            <div className="text-4xl font-serif italic tabular">{predictorScreens[2].metric}</div>
            <div className="text-xs text-white/60 mb-4">{predictorScreens[2].metricLabel}</div>
            <div className="grid grid-cols-3 gap-1.5 mt-3">
              {['#4ECDC4', '#0F1C40', '#F1EDE4', '#FF6B35', '#6366F1', '#EC4899'].map((c) => (
                <div key={c} className="aspect-square rounded" style={{ background: c, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)' }} />
              ))}
            </div>
            <div className="mt-3 text-[10px] font-mono text-white/40 uppercase">yourbrand.com → Deploy</div>
          </div>
        </div>

        {/* Pillar cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-8 lg:mt-10">
          {predictorPillars.map((p) => (
            <div key={p.id} className="bg-white border border-neutral-200 rounded-2xl p-8 hover:border-[#0F1C40] transition-colors duration-300 reveal">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">{p.tag}</div>
              <h3 className="text-2xl font-serif italic font-normal tracking-tight mb-3 text-[#0F1C40]">{p.title}</h3>
              <p className="text-sm text-[#0F1C40]/70 leading-relaxed">{p.desc}</p>
              <ArrowUpRight className="w-4 h-4 text-neutral-400 mt-6" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
